import React, { useState } from 'react';
import { RiArrowDownSLine, RiArrowUpSLine } from '@remixicon/react';
import styles from './Accordion.styles.module.css';

interface AccordionProps {
  title: React.ReactNode;
  children: React.ReactNode;
  icon?: React.ComponentType<{
    size?: string | number;
    color?: string;
    className?: string;
  }>;
  iconColor?: string;
  headerClassName?: string;
  contentClassName?: string;
  wrapperClassName?: string;
  /**
   * Custom icon element to render instead of the default icon
   * If provided, this will override the icon prop
   */
  customIcon?: React.ReactNode;
  /**
   * Custom content to render on the right side of the accordion header
   * Rendered between the title and the expand/collapse arrow
   */
  rightContent?: React.ReactNode;
  /**
   * Whether the accordion should be open by default
   * @default true
   */
  defaultOpen?: boolean;
  /**
   * Callback when accordion is opened
   * Useful for lazy loading content
   */
  onOpen?: () => void;
  /**
   * Callback when accordion is closed
   */
  onClose?: () => void;
}

const Accordion: React.FC<AccordionProps> = ({
  title,
  children,
  icon,
  iconColor,
  headerClassName,
  contentClassName,
  wrapperClassName = '',
  customIcon,
  rightContent,
  defaultOpen = true,
  onOpen,
  onClose,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const IconComponent = icon;

  const toggleAccordion = () => {
    const newOpenState = !isOpen;
    setIsOpen(newOpenState);

    // Call appropriate callback
    if (newOpenState && onOpen) {
      onOpen();
    } else if (!newOpenState && onClose) {
      onClose();
    }
  };

  return (
    <div
      className={`${styles.accordionContainer} ${wrapperClassName} ${isOpen ? styles.accordionOpen : ''}`}
      data-open={isOpen}
    >
      <div
        className={`${styles.accordionHeader} ${headerClassName} ${isOpen ? styles.accordionHeaderOpen : ''}`}
        data-open={isOpen}
        onClick={toggleAccordion}
        onKeyDown={toggleAccordion}
        role='presentation'
      >
        <span className={styles.titleWrapper}>
          {customIcon
            ? customIcon
            : IconComponent && (
                <IconComponent
                  size={16}
                  color={iconColor}
                  className={styles.icon}
                />
              )}
          <span>{title}</span>
        </span>
        <span className={styles.headerRight}>
          {rightContent}
          {isOpen ? <RiArrowUpSLine /> : <RiArrowDownSLine />}
        </span>
      </div>
      <div
        className={`${styles.accordionContent} ${
          isOpen ? styles.accordionContentOpen : ''
        } ${contentClassName}`}
      >
        {children}
      </div>
    </div>
  );
};

export default Accordion;
